import React, {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type ContentElement = React.ReactElement<{
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}>;

function contents(node: React.ReactNode): React.ReactNode[] {
  return Children.toArray(node).flatMap(child =>
    isValidElement<{ children?: React.ReactNode }>(child) && child.type === Fragment
      ? contents(child.props.children)
      : [child],
  );
}

/** Keep complete controls/cards together. Only their existing layout containers are divided. */
function contentParts(node: React.ReactNode): React.ReactNode[] {
  return contents(node).flatMap<React.ReactNode>(child => {
    if (!isValidElement(child) || typeof child.type !== "string") return [child];
    const element = child as ContentElement;
    const classes = (element.props.className || "").split(" ");
    const children = contents(element.props.children);
    if (element.type === "details" && classes.includes("role-catalogue")) {
      return [
        <ApplicationRoleCatalogue key="role-catalogue" summary={children[0]}>
          {children.slice(1)}
        </ApplicationRoleCatalogue>,
      ];
    }
    if (
      classes.some(name =>
        ["profile-step-workspace", "profile-sections-grid", "role-catalogue-body"].includes(name),
      )
    ) {
      return children.flatMap(contentParts);
    }
    if (classes.includes("section-block")) {
      const heading = children.find(item => isValidElement(item) && item.type === "h3");
      return children
        .filter(item => item !== heading)
        .flatMap(contentParts)
        .map((part, index) =>
          cloneElement(
            element,
            {
              key: `${element.key || "section"}-${index}`,
              className: `${element.props.className || ""}${index ? " application-section-continuation" : ""}`,
            },
            heading,
            part,
          ),
        );
    }
    const gridClass = classes.find(name =>
      [
        "form-grid",
        "checkbox-grid",
        "program-grid",
        "track-grid",
        "route-grid",
        "role-grid",
        "role-list",
        "review-grid",
        "cohort-list",
        "status-timeline",
        "boundary-list",
      ].includes(name),
    );
    if (gridClass) {
      const size = ["cohort-list", "program-grid", "track-grid", "route-grid"].includes(gridClass)
        ? 1
        : gridClass === "checkbox-grid"
          ? 4
          : 2;
      const groups: React.ReactNode[] = [];
      let group: React.ReactNode[] = [];
      const flush = () => {
        if (group.length)
          groups.push(cloneElement(element, { key: `${element.key || gridClass}-${groups.length}` }, group));
        group = [];
      };
      children.forEach(item => {
        const full =
          isValidElement<{ className?: string }>(item) && item.props.className?.split(" ").includes("full");
        if (full) flush();
        group.push(item);
        if (full || group.length === size) flush();
      });
      flush();
      return groups;
    }
    return [child];
  });
}

export function ApplicationStepPages({ children, label }: { children: React.ReactNode; label: string }) {
  const host = useRef<HTMLDivElement>(null);
  const visible = useRef<HTMLDivElement>(null);
  const [desktop, setDesktop] = useState(false);
  const [groups, setGroups] = useState<number[][]>([]);
  const [page, setPage] = useState(0);
  const parts = contentParts(children);

  useLayoutEffect(() => {
    const media = window.matchMedia("(min-width: 1100px) and (min-height: 740px)");
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useLayoutEffect(() => {
    if (!desktop || !host.current || !visible.current) return;
    const calculate = () => {
      if (!host.current || !visible.current) return;
      const available = Math.max(0, host.current.clientHeight - 52);
      const next: number[][] = [];
      let height = 0;
      Array.from(visible.current.children).forEach((child, index) => {
        const itemHeight = child.firstElementChild?.getBoundingClientRect().height || 0;
        if (!next.length || (height > 0 && height + 10 + itemHeight > available)) {
          next.push([]);
          height = 0;
        }
        next[next.length - 1].push(index);
        height += itemHeight + (height ? 10 : 0);
      });
      setGroups(current => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
      setPage(current => Math.min(current, Math.max(0, next.length - 1)));
    };
    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(host.current);
    Array.from(visible.current.children).forEach(child => {
      if (child.firstElementChild) observer.observe(child.firstElementChild);
    });
    return () => observer.disconnect();
  }, [children, desktop]);

  const currentGroup = groups[page] || [0];
  function changePage(next: number) {
    setPage(next);
    requestAnimationFrame(() => visible.current?.focus({ preventScroll: true }));
  }

  return (
    <div className="application-step-pages" ref={host} data-paginated={desktop || undefined}>
      {desktop && (
        <nav className="application-part-navigation" aria-label={`${label} parts`}>
          <button
            type="button"
            className="btn ghost small"
            disabled={page === 0}
            onClick={() => changePage(page - 1)}
          >
            Previous part
          </button>
          <span aria-live="polite">
            Part <strong>{page + 1}</strong> of <strong>{Math.max(1, groups.length)}</strong>
          </span>
          <button
            type="button"
            className="btn ghost small"
            disabled={page >= groups.length - 1}
            onClick={() => changePage(page + 1)}
          >
            Next part
          </button>
        </nav>
      )}
      <div
        className="application-part-content"
        ref={visible}
        role="group"
        tabIndex={-1}
        aria-label={desktop ? `${label}, part ${page + 1}` : label}
      >
        {parts.map((part, index) => (
          <div
            className="application-part-slot"
            key={index}
            data-active={!desktop || currentGroup.includes(index)}
            aria-hidden={(desktop && !currentGroup.includes(index)) || undefined}
            inert={desktop && !currentGroup.includes(index)}
          >
            <div className="application-part-item">{part}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicationRoleCatalogue({
  children,
  summary,
}: {
  children: React.ReactNode;
  summary: React.ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="role-catalogue application-catalogue-trigger"
        onClick={() => dialog.current?.showModal()}
      >
        {isValidElement<{ children?: React.ReactNode }>(summary) ? summary.props.children : summary}
      </button>
      <dialog
        ref={dialog}
        className="application-catalogue-dialog"
        aria-label="Academy role catalogue"
        onClose={() => trigger.current?.focus({ preventScroll: true })}
      >
        <div className="application-catalogue-heading">
          <h3>Academy role catalogue</h3>
          <button type="button" className="btn ghost small" onClick={() => dialog.current?.close()}>
            Back to application choices
          </button>
        </div>
        <ApplicationStepPages label="Role catalogue">{children}</ApplicationStepPages>
      </dialog>
    </>
  );
}
